import { EXTERNAL_ROUTES } from '@/constants';
import { showReviewAction } from '@/modules/common/lib/application-review';
import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { roundHalfToEven } from '@/modules/common/lib/moneyHandler';
import {
  ApplicationStage,
  ApplicationStatus,
  ApplicationTerm,
  IApplicationWithOrderAndCourse,
} from '@/types/application';
import { ICompany } from '@/types/company';
import { IUser, UserRole } from '@/types/user';
import { OpenInNew } from '@mui/icons-material';
import { Button, Chip, Tooltip, Typography } from '@mui/material';
import { Link } from '@tanstack/react-router';
import { MRT_ColumnDef } from 'material-react-table';
import pluralize from 'pluralize';
import useApplicationPayments from '../../hooks/useApplicationPayments';
import { ApplicationStatusChip } from '../ApplicationStatus/ApplicationStatusChip';
import { CourseDetailStrip } from './CourseDetailStrip';
import { ApplicationReviewerInfo } from './components/ApplicationReviewerInfo';
import { CourseCohortDetailsTooltip } from './components/CourseCohortDetailsTooltip';

const getApplicationTypes = (app: IApplicationWithOrderAndCourse) => {
  const types = [];

  if (app.order.learningBudgetAmount?.amount) {
    if (app.order.learningBudgetAmount.amount === app.order.totalAmount) {
      types.push('Learning Budget');
    } else {
      types.push('Learning Budget', app.applicationTerm);
    }
  } else {
    types.push(app.applicationTerm);
  }

  if (app.flexibleRemuneration) {
    types.push('Retribucion Flexible');
  }

  return types.map(t => (
    <Chip
      key={t}
      className={`whitespace-nowrap ${Object.values(ApplicationTerm).includes(t as ApplicationTerm) ? 'bg-gray' : 'bg-mydra-light-gray'} capitalize text-xxs`}
      label={t === ApplicationTerm.Isa ? 'ISA' : t.toLowerCase()}
      size="small"
    />
  ));
};

type GetListApplicationsTableColumnsArgs = {
  onApplicationReview: (application: IApplicationWithOrderAndCourse) => void;
  userRoles: string[];
};

export function getListApplicationsTableColumns({
  onApplicationReview,
  userRoles,
}: GetListApplicationsTableColumnsArgs): MRT_ColumnDef<IApplicationWithOrderAndCourse>[] {
  const columns: MRT_ColumnDef<IApplicationWithOrderAndCourse>[] = [];

  if (userRoles.includes(UserRole.TaxBenefitsProvider)) {
    columns.push({
      accessorKey: 'user.company.name',
      header: 'Company',
      Cell: ({ row }) => (
        <div>
          <span className="text-sm text-mydra-black break-words text-wrap">
            {((row.original.user as IUser)?.company as ICompany)?.name ?? ''}
          </span>
        </div>
      ),
    });
  }

  columns.push({
    id: 'user',
    accessorKey: 'user',
    header: 'Employee',
    muiTableHeadCellProps: {
      classes: {
        root: 'font-normal border-b-dividers normal-case text-sm',
      },
    },
    muiTableBodyCellProps: {
      classes: {
        root: 'border-b-dividers ',
      },
    },
    Cell: ({ row }) => (
      <div className="flex items-center gap-3 w-full break-words text-wrap">
        <div className="flex flex-col w-full">
          <div className="text-sm font-medium">
            <Link
              to={`/employees/${row.original.user._id}`}
            >{`${(row.original.user as IUser)?.firstName} ${(row.original.user as IUser)?.lastName}`}</Link>
          </div>
          <div className="text-xxs text-text">
            {(row.original.user as IUser).email}
          </div>
        </div>
      </div>
    ),
  });

  columns.push({
    accessorKey: 'course.name',
    header: 'Course Details',
    Cell: ({ row }) => {
      const { course, cohort: cohortId } = row.original;
      const cohort = course.cohorts?.find(cohort => cohort._id === cohortId);

      if (!cohort) {
        return null;
      }

      return (
        <CourseCohortDetailsTooltip course={course} cohort={cohort}>
          <div className="flex flex-col gap-1 w-full break-words text-wrap">
            <Typography
              className="text-mydra-purple font-semibold"
              variant="caption"
            >
              <a
                className="cursor-pointer hover:underline"
                href={EXTERNAL_ROUTES.MARKETPLACE_COURSE_URL.replace(
                  ':id',
                  course._id!,
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {course?.name}
              </a>
            </Typography>
            <Typography className="text-xs" variant="caption">
              {course?.educationProvider?.name}
            </Typography>
            <CourseDetailStrip course={course} cohort={cohort} />
          </div>
        </CourseCohortDetailsTooltip>
      );
    },
  });

  if (userRoles.includes(UserRole.EducationProvider)) {
    columns.push({
      accessorKey: 'user.profile.currentRole',
      header: 'Job Role',
      Cell: ({ row }) => (
        <Typography variant="body2">
          {row.original.user.profile?.currentRole ?? '(not informed)'}
        </Typography>
      ),
    });
  }

  if (userRoles.includes(UserRole.EducationProvider)) {
    columns.push({
      accessorKey: 'course.skills',
      header: 'Skills',
      Cell: ({ row }) => {
        const MAX_SKILLS_DISPLAYED = 3;

        return (
          <Tooltip
            classes={{
              popper: 'max-w-[500px]',
            }}
            title={
              <div className="flex flex-wrap gap-2 p-3 text-xs w-full">
                {(row.original.course?.skills ?? []).map(skillName => (
                  <Chip
                    classes={{
                      root: 'h-auto min-h-[2lh]',
                      label: 'block whitespace-normal',
                    }}
                    key={skillName}
                    label={skillName}
                  />
                ))}
              </div>
            }
          >
            <div className="flex flex-wrap gap-1 text-xs w-full">
              {(row.original.course?.skills ?? [])
                .slice(0, MAX_SKILLS_DISPLAYED)
                .map(skillName => (
                  <Chip key={skillName} label={skillName} />
                ))}
              {row.original.course?.skills?.length > MAX_SKILLS_DISPLAYED && (
                <Chip
                  label={`+${row.original.course?.skills?.length - MAX_SKILLS_DISPLAYED}`}
                />
              )}
            </div>
          </Tooltip>
        );
      },
    });
  }

  if (userRoles.includes(UserRole.Employer)) {
    columns.push({
      accessorKey: 'learningBudget',
      header: 'Requested LB',
      Cell: ({ row }) => (
        <div>
          <span className="text-sm text-mydra-black">
            {row.original.learningBudget?.amount ? (
              formatCurrencyValue(
                row.original.learningBudget.amount.toString(),
                row.original.learningBudget.currency,
              )
            ) : (
              <>&mdash;</>
            )}
          </span>
        </div>
      ),
    });
  }

  if (
    userRoles.includes(UserRole.TaxBenefitsProvider) ||
    userRoles.includes(UserRole.Employer)
  ) {
    columns.push({
      accessorKey: 'applicationTerm',
      header: 'Type',
      Cell: ({ row }) => {
        if (!row.original.order) {
          return <Chip label={row.original.applicationTerm.toLowerCase()} />;
        }

        return (
          <div className="flex flex-wrap gap-1 w-full">
            {getApplicationTypes(row.original)}
          </div>
        );
      },
    });
  }

  if (
    userRoles.includes(UserRole.TaxBenefitsProvider) ||
    userRoles.includes(UserRole.Employer)
  ) {
    columns.push({
      accessorKey: 'order.userPaymentAmount.amount',
      header: 'Payment details',
      Cell: ({ row }) => {
        if (
          !row.original.order.userPaymentAmount?.currency ||
          !Number.isFinite(row.original.order.userPaymentAmount?.amount)
        ) {
          return '-';
        }

        const totalAmount = row.original.order.userPaymentAmount?.amount;
        const numberOfInstalments =
          row.original.applicationData?.contractChoices
            ?.fixedNumberOfInstalments ?? 1;
        const amountPerInstalment = roundHalfToEven(
          totalAmount / numberOfInstalments,
        );

        return (
          <div className="flex flex-col">
            <p>
              {formatCurrencyValue(
                totalAmount,
                row.original.order.userPaymentAmount.currency,
              )}
            </p>
            {row.original.applicationTerm === ApplicationTerm.Isa && (
              <p className="text-xs">
                {numberOfInstalments}{' '}
                {pluralize('installment', numberOfInstalments)} of{' '}
                {formatCurrencyValue(
                  amountPerInstalment,
                  row.original.order.userPaymentAmount.currency,
                )}
              </p>
            )}
          </div>
        );
      },
    });
  }
  columns.push({
    accessorKey: 'statuses',
    header: 'Status',
    Cell: ({ row }) => (
      <div className="flex flex-col gap-1 w-full items-start text-wrap">
        <ApplicationStatusChip
          status={row.original.statuses?.at(-1) ?? ApplicationStatus.Applied}
          stage={row.original.applicationStage}
        />
        {![ApplicationStage.Applying, ApplicationStage.ReviewRequired].includes(
          row.original.applicationStage ?? ApplicationStage.Applying,
        ) && (
          <ApplicationReviewerInfo
            application={row.original}
            userRoles={userRoles as UserRole[]}
          />
        )}
      </div>
    ),
  });

  columns.push({
    accessorKey: 'applicationStage',
    header: 'Stage',
    Cell: ({ row }) => {
      return <div className="text-xs">{row.original.applicationStage}</div>;
    },
  });

  columns.push({
    id: 'actions',
    header: 'Actions',
    maxSize: 100,
    muiTableHeadCellProps: {
      align: 'right',
      classes: {
        root: 'font-normal border-b-dividers  normal-case text-sm',
      },
    },
    muiTableBodyCellProps: {
      align: 'right',
      classes: {
        root: 'border-b  border-b-dividers',
      },
    },
    Cell: ({ row }) => {
      const isReviewActionVisible = showReviewAction(row.original, userRoles);
      const { showPayNowButton } = useApplicationPayments(row);

      return (
        <div
          className={`${isReviewActionVisible ? 'flex gap-2 ml-auto' : 'w-full'}`}
        >
          <div className={isReviewActionVisible ? 'ml-auto' : 'text-right'}>
            {isReviewActionVisible && (
              <Tooltip
                title={
                  !showReviewAction(row.original, userRoles)
                    ? 'No pending reviews'
                    : ''
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      onApplicationReview(row.original);
                    }}
                    disabled={!showReviewAction(row.original, userRoles)}
                  >
                    Review
                  </Button>
                </span>
              </Tooltip>
            )}
            {showPayNowButton && (
              <Button
                size="small"
                variant="outlined"
                className="shrink-0 w-max"
                href="/payments"
                startIcon={<OpenInNew />}
              >
                Pay now
              </Button>
            )}
          </div>
        </div>
      );
    },
  });

  return columns;
}
